import { Gender } from '../../techstyle-shared/react-accounts';
import { getBrandValue, isSavage } from '../utils/brandNameHelper';

/**
 * This is a list of feature flag names that override the default `featureNames` values and
 * is intended to be temporary. See note in `featureNames` about the use of kebab case
 * for the flag name. A flag will temporarily be in both `featureNames` and
 * `savageFeatureNameOverrides` until it is fully migrated to LaunchDarkly, at which point
 *  it can be removed from `savageFeatureNameOverrides`.
 */
const savageFeatureNameOverrides = {
  ACCESSIBILITY_AI_WIDGET: 'accessibility-ai-widget',
  ALLOW_FRONTEND_LOGGING: 'allow-frontend-logging',
  ALLOW_USER_UPLOADED_REVIEW_PHOTOS: 'allow-user-uploaded-review-photos',
  BOUNCEBACK_ENDOWMENT: 'bounceback-endowment',
  BUILD_YOUR_OWN_BUNDLE: 'build-your-own-bundle',
  CONSTRUCTOR_GRID_V1_1: 'use-constructor-grid-v1-1',
  CONSTRUCTOR_SEARCH: 'constructor-search',
  COGNIGY_WEB_CHAT: 'cognigy-widget',
  FIRST_MEMBER_BILLING_MODAL: 'fmbe-pop-up-web',
  GENESYS_WEB_CHAT: 'genesys-web-chat',
  HDYH_TIERED_LIST: 'hdyh-tiered-list',
  INCENTIVIZED_REVIEWS_UI: 'incentivized-reviews-ui',
  PDP_VIDEO_CAROUSEL: 'pdp-video-carousel',
  POSTREG_TIMER: 'countdown-timer',
  PROMO_PICKER_SIGNUP_MODAL: 'gift-box-sign-up-modal',
  PROMO_PICKER_SMS: 'gift-box-sign-up-modal-sms',
  SHOPPABLE_VIDEO: 'shoppable-video',
  SINGLE_CTA_VIP_OPT_IN: 'experiment-pdp-single-cta',
  SHOULD_USE_IMAGE_SERVICE_URL: 'use-image-service-url',
  USE_PRICING_SERVICE: 'use-pricing-service',
  MEMBER_CREDIT_PRICE_TEST: 'SXF-MC-TARIFF-TEST',
} as const;

/**
 * NOTE: When updating a flag to be handled by LaunchDarkly use kebab case for the flag name
 * and use the flag name in `savageFeatureNameOverrides` if applicable. At the same time,
 * that key can be removed from the `savageFeatureNameOverrides` object.
 * The end goal is to have a single `featureNames` object without any overrides so that we have
 * uniform feature flag names across all brands.
 *
 * NOTE: Currently the JSDoc comments are linking to `/projects/default` for the SXF flags, I am
 * unsure how this works once we have access to both environments. If the format changes, it should
 * be a simple find and replace once we have more information.
 */
const featureNames = {
  /**
   * @description Controls whether the accessibility widget is enabled on the site.
   */
  ACCESSIBILITY_AI_WIDGET: 'accessibility_ai_widget',
  /**
   * @description Controls whether Afterpay, Klarna, both or neither payment methods
   * are available to the customer.
   *
   * - {@link https://app.launchdarkly.com/projects/default/flags/additional-payment-methods/ SXF LaunchDarkly configuration }
   */
  ADDITIONAL_PAYMENT_METHODS: 'additional-payment-methods',
  AFTERPAY: 'afterpay',
  /**
   * @description Controls whether errors will be logged to Graylog using the `/logging` endpoint.
   *
   * - {@link https://app.launchdarkly.com/projects/default/flags/allow-frontend-logging/ SXF LaunchDarkly configuration }
   */
  ALLOW_FRONTEND_LOGGING: 'allow_frontend_logging',
  /**
   * @description Controls if customers can upload photos to with their reviews.
   *
   * - {@link https://app.launchdarkly.com/projects/default/flags/allow-user-uploaded-review-photos/ SXF LaunchDarkly configuration }
   */
  ALLOW_USER_UPLOADED_REVIEW_PHOTOS: 'allow_user_uploaded_review_photos',
  /**
   * @description Controls whether the Bounceback Endowment feature is enabled.
   *
   * - {@link https://app.launchdarkly.com/projects/default/flags/bounceback-endowment/ SXF LaunchDarkly configuration }
   */
  BOUNCEBACK_ENDOWMENT: 'bounceback_endowment',
  BOT_TEST: 'bot_test',
  /**
   * @description Controls whether the Build Your Own Bundle feature is enabled.
   *
   * - {@link https://app.launchdarkly.com/projects/default/flags/build-your-own-bundle/ SXF LaunchDarkly configuration }
   */
  BUILD_YOUR_OWN_BUNDLE: 'build_your_own_bundle',
  CANCEL_OFFER_HIGH: 'cancel_offer_high_50',
  CANCEL_OFFER_LOW: 'cancel_offer_low_20',
  CANCEL_OFFER_MID: 'cancel_offer_mid_30',
  CANCEL_OFFER_VERY_LOW: 'cancel_offer_very_low_10',
  CANCEL_ORDER_POPUP: 'cancel_order_popup',
  /**
   * @description This feature flag controls whether Constructor Search is enabled.
   * @deprecated This feature flag is no longer used in the codebase and can probably be removed.
   *
   * - {@link https://app.launchdarkly.com/projects/default/flags/constructor-search/ SXF LaunchDarkly configuration }
   */
  CONSTRUCTOR_SEARCH: 'use_constructorio_search',
  /**
   * @description This feature flag controls whether Constructor Grid Revamp V1.1 is enabled.
   *
   * - {@link https://app.launchdarkly.com/projects/default/flags/use-constructor-grid-v1-1/ SXF LaunchDarkly configuration }
   */
  CONSTRUCTOR_GRID_V1_1: 'use_constructor_grid_v1_1',
  DENIM_INTEREST: 'denim_interest',
  FIRST_MEMBER_BILLING_MODAL: 'fmbe_pop_up_web',
  GENDER_AUTO_FILTER: 'gender_auto_filter',
  /**
   * @description Controls whether the Genesys is used as the web chat widget on the site.
   *
   * - {@link https://app.launchdarkly.com/projects/default/flags/genesys-web-chat/ SXF LaunchDarkly configuration }
   */
  GENESYS_WEB_CHAT: 'genesys_web_chat',
  COGNIGY_WEB_CHAT: 'cognigy_web_chat',
  KHLOE_SKIP_THE_MONTH: 'khloe_skip_the_month',
  KLARNA: 'klarna',
  LOYALTY_PROGRAM_FL: 'loyalty_program_fl',
  LOYALTY_PROGRAM_YITTY: 'loyalty_program_yitty',
  MEMBERSHIP_5995_PRICE: 'membership_5995_price',
  ONLINE_CANCEL_POPUP: 'online_cancel_chat_popup',
  POSTREG_TIMER: 'postreg_timer',
  PRIORITIZED_PROMOS: 'prioritized_promos',
  QUIZ_BIRTHDAY_AB_TEST_FEATURE: 'fabletics_quiz_birthday_ab_test',
  RETAILPRICETEST: 'RETAILPRICETEST',
  RETURN_IN_STORE: 'return_in_store',
  SCRUBS: 'scrubs',
  SHOW_OOS: 'show_oos',
  SINGLE_CTA_VIP_OPT_IN: 'single_cta_vip_opt_in',
  SKIP_TEST: 'skip_test',
  SURFACE_HIDDEN_FACETS: 'surface-hidden-facets',
  TARIFF: 'tariff',
  TARIFF_VIP: 'tariff_vip',
  TEXT_EMBROIDERY: 'text_embroidery',
  USPS_RMA: 'usps_rma',
  ENABLE_PDP_VIDEO_DESCRIPTIONS: 'web_enable_pdp_video_descriptions',
  PASSWORDLESS_LOGIN: 'passwordless_login',
  PASSWORDLESS_SIGNUP: 'passwordless_signup',
  PROMO_PICKER_DEBUG: 'promo_picker_debug',
  GEN_FREESHIP_PROMOS: 'gen_freeship_promos',
  /**
   * @description Controls whether the Promo Picker sign up modal (PPP, aka "Gift Box Sign Up Modal") is enabled.
   *
   * - {@link https://app.launchdarkly.com/projects/default/flags/gift-box-sign-up-modal/ SXF LaunchDarkly configuration }
   */
  PROMO_PICKER_SIGNUP_MODAL: 'ppp_sizes',
  /**
   * @description Controls whether the Promo Picker sign up modal (PPP, aka "Gift Box Sign Up Modal") asks for SMS number
   * during sign up.
   *
   * - {@link https://app.launchdarkly.com/projects/default/flags/gift-box-sign-up-sms/ SXF LaunchDarkly configuration }
   */
  PROMO_PICKER_SMS: 'ppp_sms',
  WAREHOUSE_OUTLET_SITE: 'warehouse_outlet_site',
  SAVE_OFFER_SEGMENTATION: 'save_offer_segmentation',
  /**
   * @description Controls whether the pricing service is used for GraphQL product queries.
   *
   * - {@link https://app.launchdarkly.com/projects/default/flags/use-pricing-service/ SXF LaunchDarkly configuration }
   */
  USE_PRICING_SERVICE: 'use_pricing_service',
  SNOOZE_AB_TEST: 'snooze_ab_test',
  /**
   * @description Controls whether the dropdown is the "tiered" style for "How did you hear?"
   *
   * - {@link https://app.launchdarkly.com/projects/default/flags/hdyh-tiered-list/ SXF LaunchDarkly configuration }
   */
  HDYH_TIERED_LIST: 'hdyh_tiered_list',
  /**
   * @description Controls whether the `/shoppable-video/feed` page is enabled and allowed to be accessed
   * on mobile resolution.
   *
   * - {@link https://app.launchdarkly.com/projects/default/flags/shoppable-video/ SXF LaunchDarkly configuration }
   */
  SHOPPABLE_VIDEO: 'shoppable_video',
  SHOP_BY_COLOR: 'shop_by_color',
  /**
   * @description Controls whether the image service is used for product images
   *
   * - {@link https://app.launchdarkly.com/projects/default/flags/use-image-service-url/ SXF LaunchDarkly configuration }
   */
  SHOULD_USE_IMAGE_SERVICE_URL: 'use_image_service_url',
  /**
   * @description Controls which click ids are tracked as query params and sent with `identify` calls
   * through Segment when logged in.
   *
   * - {@link https://app.launchdarkly.com/projects/default/flags/tracked-click-ids/ SXF LaunchDarkly configuration }
   */
  TRACKED_CLICK_IDS: 'tracked-click-ids',
  WEB_ENABLE_POINTS_FOR_ACTIONS: 'web_enable_points_for_actions',
  /**
   * @description Controls whether "incentivized reviews" UI is displayed.
   *
   * - {@link https://app.launchdarkly.com/projects/default/flags/incentivized-reviews-ui/ SXF LaunchDarkly configuration }
   */
  INCENTIVIZED_REVIEWS_UI: 'incentivized_reviews_ui',
  YITTY_CHECKOUT_INTERSTITIAL: 'yitty_checkout_interstitial',
  FL_REMOVED_CHECKOUT_INTERSTITIAL: 'fl_removed_checkout_interstitial',
  PAYGO_RESTOCKING_FEE: 'paygo-restocking-fee',
  REMOVE_ORIGIN: 'remove_origin',
  SMS_QUIZ: 'sms_quiz',
  SHIPPING_THRESHOLD_UI_MINICART: 'shipping_threshold_ui_minicart',
  /**
   * @description Controls whether the video carousel is displayed on the PDP (as seen on you section)
   *
   * - {@link https://app.launchdarkly.com/projects/default/flags/pdp-video-carousel/ SXF LaunchDarkly configuration }
   */
  PDP_VIDEO_CAROUSEL: 'pdp_video_carousel',
  /**
   * @description Controls whether user will see free shipping progress bar in shopping bag page
   *
   * - {@link https://app.launchdarkly.com/projects/default/flags/free-shipping-progress/ SXF LaunchDarkly configuration }
   */
  FREE_SHIPPING_PROGRESS: 'free-shipping-progress',
  /**
   * @description Controls whether order history, react checkout confirmation, etc. uses the image service
   *
   */
  USE_IMAGE_SERVICE_MISC: 'use_image_service_misc',
  ...(isSavage() ? savageFeatureNameOverrides : {}),
} as const;

const savageStaticFeatureValidators = {
  [featureNames.BOT_TEST]: false,
  [featureNames.CANCEL_OFFER_HIGH]: false,
  [featureNames.CANCEL_OFFER_LOW]: false,
  [featureNames.CANCEL_OFFER_MID]: false,
  [featureNames.CANCEL_OFFER_VERY_LOW]: false,
  [featureNames.CANCEL_ORDER_POPUP]: false,
  [featureNames.DENIM_INTEREST]: false,
  [featureNames.KHLOE_SKIP_THE_MONTH]: false,
  [featureNames.LOYALTY_PROGRAM_FL]: false,
  [featureNames.LOYALTY_PROGRAM_YITTY]: false,
  [featureNames.MEMBERSHIP_5995_PRICE]: false,
  [featureNames.PRIORITIZED_PROMOS]: true,
  [featureNames.QUIZ_BIRTHDAY_AB_TEST_FEATURE]: false,
  [featureNames.RETURN_IN_STORE]: false,
  [featureNames.SCRUBS]: false,
  [featureNames.SHOW_OOS]: true,
  [featureNames.TEXT_EMBROIDERY]: false,
  [featureNames.USPS_RMA]: true,
  [featureNames.ENABLE_PDP_VIDEO_DESCRIPTIONS]: false,
  [featureNames.PASSWORDLESS_LOGIN]: false,
  [featureNames.PASSWORDLESS_SIGNUP]: false,
  [featureNames.WAREHOUSE_OUTLET_SITE]: false,
  [featureNames.SAVE_OFFER_SEGMENTATION]: false,
  [featureNames.SNOOZE_AB_TEST]: false,
  [featureNames.SHOP_BY_COLOR]: false,
  [featureNames.WEB_ENABLE_POINTS_FOR_ACTIONS]: false,
  [featureNames.YITTY_CHECKOUT_INTERSTITIAL]: false,
  [featureNames.FL_REMOVED_CHECKOUT_INTERSTITIAL]: false,
};

/**
 * These are features that can be statically disabled by default
 * (i.e. FL/YT) because it is an SXF exclusive feature.
 * These can be found in SXF LaunchDarkly so we only want to statically disable
 * them if we are not in SXF.
 */
const defaultExclusiveFeatureValidators = {
  [featureNames.ADDITIONAL_PAYMENT_METHODS]: false,
  [featureNames.TRACKED_CLICK_IDS]: false,
};

/*
  adding a validator to this object will circumvent feature creature
  and will not make an API call to retrieve these values when using
  local utilities getHasFeature and useHasFeature.

  READ BEFORE ADDING TO THIS LIST:
  When moving features into this list: please validate with a staff
  engineer that this is the appropriate place to put your feature
  and that the feature is now considered a statically defined feature.

  If adding a feature to this list that is permanently enabled or
  disabled: please create a tech debt or squad ticket to clean up
  the path that is no longer used. Please note the deprecation ticket
  in a comment above your feature.
*/
export const staticFeatureValidators = {
  [featureNames.GENDER_AUTO_FILTER]: ({ session }) => {
    return session.isLoggedIn;
  },
  [featureNames.KHLOE_SKIP_THE_MONTH]: ({ gender }) => {
    return gender === Gender.FEMALE;
  },
  [featureNames.LOYALTY_PROGRAM_FL]: ({ membership }) => {
    return membership.isVip;
  },
  [featureNames.LOYALTY_PROGRAM_YITTY]: ({ membership, tld }) => {
    return tld === '.com' && membership.isVip;
  },
  [featureNames.MEMBERSHIP_5995_PRICE]: ({ tld }) => {
    return ['.com', '.ca', '.co.uk'].includes(tld);
  },
  [featureNames.QUIZ_BIRTHDAY_AB_TEST_FEATURE]: ({ tld }) => {
    return ['.com'].includes(tld);
  },
  [featureNames.RETAILPRICETEST]: ({ membership }) => {
    return !membership.isVip;
  },
  [featureNames.RETURN_IN_STORE]: ({ tld }) => {
    return ['.com'].includes(tld);
  },
  [featureNames.TEXT_EMBROIDERY]: ({ tld }) => {
    return ['.com'].includes(tld);
  },
  [featureNames.PROMO_PICKER_DEBUG]: () =>
    window?.location.hostname === 'localhost',
  ...(isSavage()
    ? savageStaticFeatureValidators
    : defaultExclusiveFeatureValidators),
};

export const globalPrefetchedFeatures = [
  featureNames.SCRUBS,
  featureNames.POSTREG_TIMER,
  featureNames.ALLOW_FRONTEND_LOGGING,
  featureNames.BUILD_YOUR_OWN_BUNDLE,
  featureNames.BOUNCEBACK_ENDOWMENT,
  featureNames.USE_PRICING_SERVICE,
  featureNames.PASSWORDLESS_LOGIN,
  featureNames.PASSWORDLESS_SIGNUP,
  featureNames.COGNIGY_WEB_CHAT,
  featureNames.WAREHOUSE_OUTLET_SITE,
  featureNames.HDYH_TIERED_LIST,
  featureNames.SHOP_BY_COLOR,
  featureNames.SHOULD_USE_IMAGE_SERVICE_URL,
  featureNames.INCENTIVIZED_REVIEWS_UI,
  featureNames.REMOVE_ORIGIN,
  featureNames.TARIFF,
  featureNames.TARIFF_VIP,
  featureNames.GEN_FREESHIP_PROMOS,
];

export const prefetchedFeaturesInLaunchDarkly = getBrandValue({
  default: [],
  savage: [
    featureNames.POSTREG_TIMER,
    featureNames.ALLOW_FRONTEND_LOGGING,
    featureNames.BUILD_YOUR_OWN_BUNDLE,
    featureNames.BOUNCEBACK_ENDOWMENT,
    featureNames.USE_PRICING_SERVICE,
    featureNames.HDYH_TIERED_LIST,
    featureNames.COGNIGY_WEB_CHAT,
    featureNames.AFTERPAY,
    featureNames.KLARNA,
    featureNames.SINGLE_CTA_VIP_OPT_IN,
    featureNames.SHOULD_USE_IMAGE_SERVICE_URL,
    featureNames.INCENTIVIZED_REVIEWS_UI,
  ],
});

export default featureNames;
